import { useEffect, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { formatParty, isLocationType, venueVocabulary } from '@/venue/venue';
import type { ReadyOrder } from './api';
import { EdgeBar, Notice } from './parts';
import { useReadyOrders, useServeOrder, useStaffLive } from './queries';
import {
  describeStaffError,
  locationText,
  locationTypeOf,
  markServedLabel,
  portionsText,
  serveWord,
  waitedText,
} from './wording';
import { PageHeader } from '@/components/PageHeader';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';

/** Redraws "5 min ago" twice a minute. When the order became ready comes from the server. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** "#0042" from "ORD-0042". Any other form is shown as the backend sent it. */
function orderLabel(orderNumber: string): string {
  const match = /^ORD-(\d+)$/.exec(orderNumber);
  return match ? `#${match[1]}` : orderNumber;
}

/**
 * One finished order: a green bar on the left edge, the place as the title, the lines, and one "Mark served".
 * The line and portion counts under the button are counts of the lines the backend sent, never money.
 */
function ReadyCard({ order, now }: { order: ReadyOrder; now: number }) {
  const serve = useServeOrder();
  const type = locationTypeOf(order.location_type);
  const where = locationText(order.location_type, order.location_label);
  const word = serveWord(order.location_type);
  const party = isLocationType(order.location_type)
    ? formatParty(order.location_type, order.party_name)
    : order.party_name;
  const toServe = order.items.filter((item) => item.fulfillment_status === 'READY');

  return (
    <li
      className={cn(
        cardClasses({ padding: 'none' }),
        'relative flex flex-col gap-3 overflow-hidden py-3 pr-3 pl-4',
      )}
    >
      <EdgeBar tone="success" />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-xl leading-6 font-bold break-words text-ink">{where}</h2>
          <p className="mt-0.5 text-xs break-words text-muted">
            {orderLabel(order.order_number)} · {party}
            {order.zone_name ? ` · ${order.zone_name}` : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge tone="success">Ready</Badge>
          <span className="text-xs text-faint">{waitedText(order.ready_at, now)}</span>
        </div>
      </div>

      <ul className="flex flex-col divide-y divide-line">
        {order.items.map((item) => {
          const done = item.fulfillment_status !== 'READY';
          return (
            <li key={item.order_item_id} className="flex flex-col gap-0.5 py-2">
              <span className="flex items-start justify-between gap-3">
                <span
                  className={cn(
                    'min-w-0 text-base font-semibold break-words',
                    done ? 'text-faint' : 'text-ink',
                  )}
                >
                  <span className="tabular-nums">{item.quantity}×</span> {item.name}
                </span>
                {done && <Badge tone="neutral">{venueVocabulary(type).servedShort}</Badge>}
              </span>
              {item.modifiers.length > 0 && (
                <span className="text-sm break-words text-muted">{item.modifiers.join(', ')}</span>
              )}
              {item.notes && (
                <span className="text-sm break-words text-attention-ink italic">
                  Note: {item.notes}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {order.notes && (
        <p className="rounded-control bg-attention-soft px-3 py-1.5 text-sm break-words text-attention-ink">
          <span className="font-semibold">Order note:</span> {order.notes}
        </p>
      )}

      {serve.error && <Notice tone="error">{describeStaffError(serve.error, 'serve')}</Notice>}

      <div className="flex flex-col gap-1">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={serve.isPending}
          aria-label={`${word} ${orderLabel(order.order_number)} at ${where}`}
          onClick={() =>
            serve.mutate({
              orderId: order.order_id,
              doneMessage: `${where}: ${orderLabel(order.order_number)} ${venueVocabulary(type).servedShort.toLowerCase()}.`,
            })
          }
        >
          {markServedLabel(order.location_type)}
        </Button>
        {toServe.length > 0 && (
          <p className="text-center text-sm text-muted tabular-nums">
            {portionsText(
              toServe.length,
              toServe.reduce((n, item) => n + item.quantity, 0),
            )}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Orders the kitchen has finished, waiting to go to the table or seat. The kitchen marks food ready; the
 * waiter serves it here. The backend decides: only a READY order can be served, and a second press, or a
 * second waiter, cannot serve it twice.
 */
export function ReadyPage() {
  useStaffLive();
  const ready = useReadyOrders();
  const now = useNow();
  const list = ready.data ?? [];

  return (
    <section aria-labelledby="ready-title" className="flex flex-col gap-4">
      <PageHeader
        id="ready-title"
        title="Ready to serve"
        actions={
          ready.data &&
          list.length > 0 && (
            <p className="text-sm text-muted" role="status" aria-live="polite">
              {list.length === 1 ? '1 order waiting' : `${list.length} orders waiting`}
            </p>
          )
        }
      />
      {ready.isPending && (
        <LoadingRegion slowHint label="Loading ready orders">
          <div className="grid gap-3 md:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </LoadingRegion>
      )}
      {ready.isError && !ready.data && (
        <ErrorState error={ready.error} onRetry={() => void ready.refetch()} />
      )}
      {ready.isError && ready.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void ready.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the list. Showing the last one received.
        </Notice>
      )}
      {ready.data && list.length === 0 && (
        <EmptyState
          icon="check"
          title="Nothing to serve right now"
          message="Orders appear here as soon as the kitchen marks them ready."
        />
      )}
      {list.length > 0 && (
        // Cards fill the width: one card is as wide as the screen, more share it from 20rem each.
        <ul className="grid items-start gap-3 md:grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))]">
          {list.map((order) => (
            <ReadyCard key={order.order_id} order={order} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}
